import { useState, useEffect, useRef, useMemo } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import axios from "../interseptor/axiosConf";
import { useOutletContext } from 'react-router-dom';
import {
	DecoupledEditor,
	FileRepository,
	AccessibilityHelp,
	Alignment,
	Autoformat,
	AutoImage,
	AutoLink,
	Autosave,
	BalloonToolbar,
	BlockToolbar,
	Bold,
	CloudServices,
	Code,
	CodeBlock,
	Essentials,
	FindAndReplace,
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
	Heading,
	HorizontalLine,
	ImageBlock,
	ImageCaption,
	ImageInline,
	ImageInsertViaUrl,
	ImageResize,
	ImageStyle,
	ImageTextAlternative,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	LinkImage,
	List,
	ListProperties,
	Mention,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	RemoveFormat,
	SelectAll,
	SpecialCharacters,
	SpecialCharactersArrows,
	SpecialCharactersCurrency,
	SpecialCharactersEssentials,
	SpecialCharactersLatin,
	SpecialCharactersMathematical,
	SpecialCharactersText,
	Strikethrough,
	Subscript,
	Superscript,
	Table,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar,
	TextTransformation,
	TodoList,
	Underline,
	Undo
} from 'ckeditor5';

import './Editor.css';
import 'ckeditor5/ckeditor5.css';
import 'prismjs/themes/prism-okaidia.css';

export default function Editor({ editorContent, setEditorContent, initialContents }) {
	const editorRef = useRef(null);
	const editorContainerRef = useRef(null);
	const editorMenuBarRef = useRef(null);
	const editorToolbarRef = useRef(null);
	const beURL = localStorage.getItem('BE_Domain')



	const [isLayoutReady, setIsLayoutReady] = useState(false);


	useEffect(() => {
		setIsLayoutReady(true);
	
		return () => setIsLayoutReady(false); //clean on unmount
	}, [initialContents]);

		const editorConfig = {
        toolbar: {
            items: [
                'undo', 'redo', '|', 'heading', '|', 'fontSize', 'fontFamily', 'fontColor',
                'fontBackgroundColor', '|', 'bold', 'italic', 'underline', '|', 'link',
                'insertTable', 'codeBlock', '|', 'alignment', '|', 'bulletedList',
                'numberedList', 'todoList', 'outdent', 'indent'
            ],
            shouldNotGroupWhenFull: false
        },
        plugins: [
            FileRepository, CustomUploadAdapterPlugin, AccessibilityHelp, Alignment, Autoformat,
            AutoImage, AutoLink, Autosave, BalloonToolbar, BlockToolbar, Bold, CloudServices, Code,
            CodeBlock, Essentials, FindAndReplace, FontBackgroundColor, FontColor, FontFamily,
            FontSize, Heading, HorizontalLine, ImageBlock, ImageCaption, ImageInline,
            ImageInsertViaUrl, ImageResize, ImageStyle, ImageTextAlternative, ImageToolbar,
            ImageUpload, Indent, IndentBlock, Italic, Link, LinkImage, List, ListProperties,
            Mention, PageBreak, Paragraph, PasteFromOffice, RemoveFormat, SelectAll,
            SpecialCharacters, SpecialCharactersArrows, SpecialCharactersCurrency,
            SpecialCharactersEssentials, SpecialCharactersLatin, SpecialCharactersMathematical,
            SpecialCharactersText, Strikethrough, Subscript, Superscript, Table, TableCaption,
            TableCellProperties, TableColumnResize, TableProperties, TableToolbar,
            TextTransformation, TodoList, Underline, Undo
        ],
        image: {
            toolbar: [
                'toggleImageCaption', 'imageTextAlternative', '|', 'imageStyle:inline',
                'imageStyle:wrapText', 'imageStyle:breakText', '|', 'resizeImage'
            ]
        },
        initialData: initialContents,  // Updated initaialData
        placeholder: 'Type or paste your content here!'
	}

	class CustomUploadAdapter {
		constructor(loader) {
			this.loader = loader;
			this.url = `/api/upload-image`;
		}

		upload() {
			return this.loader.file
				.then((file) => {
					const data = new FormData();
					data.append('upload', file);
		
					return axios.post(this.url, data, {
						headers: {
							'Content-Type': 'multipart/form-data',
						}
					})
					.then(response => {
						if (response.status !== 200) {
							throw new Error('Image upload failed.');
						}
						// Push uploaded file URL to the array
						uploadedFiles.push(response.data.fileURL);
		
						// CKEditor expects this structure
						return {
							default: response.data.fileURL // Return the uploaded image URL
						};
					})
					.catch(error => {
						console.error('Error uploading image:', error);
						throw error;
					});
				});
		}

		abort() {
			// Handle any necessary cleanup here if needed
		}
	}


	function CustomUploadAdapterPlugin(editor) {
		editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
			return new CustomUploadAdapter(loader);
		};
	}

	let uploadedFiles = [];
	function detectDeletedFiles(newContent) {
		// Extract file URLs from the editor content
		const currentFiles = extractFileURLs(newContent);

		// Compare with the uploaded files to detect deleted ones
		const deletedFiles = uploadedFiles.filter(file => !currentFiles.includes(file));

		// Send a request to the server to remove deleted files
		deletedFiles.forEach(file => {
			axios.post(`/api/delete-image`, {
				filePath: file
			}, {
				headers: {
					'Content-Type': 'application/json',
				}
			})
			.then(response => {
				// Handle the response if needed
				console.log(`File ${file} deleted successfully`, response.data);
			})
			.catch(error => {
				// Handle any error if the request fails
				console.error(`Error deleting file ${file}:`, error);
			});
		});

		function extractFileURLs(content) {
			const regex = /<img[^>]+src="([^">]+)"/g;
			let matches, urls = [];
			while (matches = regex.exec(content)) {
				urls.push(matches[1]);
			}
			return urls;
		}

		// Update the uploaded files to reflect the current state
		uploadedFiles = currentFiles;
	}

	const sanitizeContent = (html) => {
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, 'text/html');

		// Remove filler elements
		doc.querySelectorAll('[data-cke-filler="true"]').forEach(el => el.remove());

		return doc.body.innerHTML;
	};

	//getting header Height
	const { headerHeight } = useOutletContext();
	return (
		<div>
			<div className="main-container">
				<div className="editor-container editor-container_document-editor" ref={editorContainerRef}>
					<div className="editor-container__menu-bar" ref={editorMenuBarRef}></div>
					<div className="editor-container__toolbar" ref={editorToolbarRef} style={{ top: headerHeight }}></div>
					<div className="editor-container__editor-wrapper">
						<div className="editor-container__editor">
							<div ref={editorRef}>
								{isLayoutReady && (
									<CKEditor
										onReady={editor => {
											editorToolbarRef.current.appendChild(editor.ui.view.toolbar.element);
											editorMenuBarRef.current.appendChild(editor.ui.view.menuBarView.element);
											editor.model.document.on('change:data', () => {

												if (editor && editor.getData) {
													const data = editor.getData();
													setEditorContent(data);
												}
											});
										}}
										onChange={(event, editor) => {
											try {
												if (editor && typeof editor.getData === 'function') {
													const rawData = editor.getData();
													const cleanedData = sanitizeContent(rawData);

													if (cleanedData) {
														detectDeletedFiles(cleanedData); // Detect and handle deleted files
														setEditorContent(cleanedData);
													}
												} else {
													console.warn('Editor instance is not ready or getData method is unavailable.');
												}
											} catch (error) {
												console.error('Error while getting editor data:', error);
											}
										}}

										onAfterDestroy={() => {
											Array.from(editorToolbarRef.current.children).forEach(child => child.remove());
											Array.from(editorMenuBarRef.current.children).forEach(child => child.remove());
										}}
										editor={DecoupledEditor}
										config={editorConfig}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
